import type { PrintableDocument, BusinessInfo } from './types'
import { THERMAL_WIDTH, LINE_PREFIXES } from './types'
import type { Order, OrderSummary, OrderCartItem } from '~/types/orders'

/**
 * Transforms an order into a printable document
 * Everything is pre-formatted - printer just processes lines
 */
export function transformOrderToDocument(
  order: Order | OrderSummary,
  businessInfo?: BusinessInfo,
  width: number = THERMAL_WIDTH.SMALL,
  t?: (key: string) => string,
): PrintableDocument {
  const lines: string[] = []

  // Helper function for translations with fallbacks
  const translate = (key: string, fallback: string) => t?.(key) || fallback

  // Handle both Order and OrderSummary types
  const orderData = 'order' in order ? order.order : order

  // HEADER - Business Information
  lines.push('='.repeat(width))
  if (businessInfo?.name) {
    lines.push(`${LINE_PREFIXES.HEADER}${businessInfo.name}`)
  }
  if (businessInfo?.address) {
    lines.push(`${LINE_PREFIXES.CENTER}${businessInfo.address}`)
  }
  if (businessInfo?.phone) {
    lines.push(`${LINE_PREFIXES.CENTER}${businessInfo.phone}`)
  }
  lines.push('='.repeat(width))
  lines.push('')

  // ORDER INFORMATION
  lines.push(`${translate('print.receipt.order', 'Order')}: ${orderData.id.slice(-8)}`) // Last 8 chars of UUID
  lines.push(`${translate('print.receipt.date', 'Date')}: ${formatDateTime(orderData.created_at)}`)

  // CLIENT INFORMATION
  if (orderData.client_details?.name) {
    lines.push('')
    lines.push(`${translate('print.receipt.clientInfo', 'CLIENT INFO')}:`)
    lines.push(`${translate('print.receipt.name', 'Name')}: ${orderData.client_details.name}`)

    if (orderData.client_details.email) {
      lines.push(`${translate('print.receipt.email', 'Email')}: ${orderData.client_details.email}`)
    }
    if (orderData.client_details.phone) {
      lines.push(`${translate('print.receipt.phone', 'Phone')}: ${orderData.client_details.phone}`)
    }
    if (orderData.client_details.address_line_1) {
      lines.push(`${translate('print.receipt.address', 'Address')}: ${orderData.client_details.address_line_1}`)
    }
    if (orderData.client_details.address_line_2) {
      lines.push(`         ${orderData.client_details.address_line_2}`)
    }
    if (orderData.client_details.city) {
      lines.push(`${translate('print.receipt.city', 'City')}: ${orderData.client_details.city}`)
    }
    if (orderData.client_details.state) {
      lines.push(`${translate('print.receipt.state', 'State')}: ${orderData.client_details.state}`)
    }
    if (orderData.client_details.postal_code) {
      lines.push(`${translate('print.receipt.postal', 'Postal')}: ${orderData.client_details.postal_code}`)
    }
  }

  if (orderData.notes) {
    lines.push(`${translate('print.receipt.notes', 'Notes')}: ${orderData.notes}`)
  }

  lines.push('-'.repeat(width))

  // ITEMS HEADER
  lines.push(`${translate('print.receipt.item', 'Item').padEnd(20)} ${translate('print.receipt.qty', 'Qty').padStart(3)}   ${translate('print.receipt.price', 'Price')}`)
  lines.push('-'.repeat(width))

  // ITEMS - Handle both regular products and combos
  let subtotal = 0
  orderData.cart.forEach((cartItem: OrderCartItem) => {
    const itemPrice = cartItem.base_price || 0
    subtotal += itemPrice * (cartItem.quantity || 1)

    if ((cartItem.type === 'COMBO' || cartItem.type === 'CUSTOM_COMBO') && (cartItem as any).items) {
      // COMBO: Show combo name first, then individual items
      const qty = cartItem.quantity || 1
      const basePrice = itemPrice
      let modifiersTotal = 0

      const mainLine = formatItemLine(
        `${cartItem.name} Combo`,
        qty,
        basePrice,
        width,
      )
      lines.push(mainLine)

      // Process each item in the combo
      const comboItems = (cartItem as any).items
      comboItems.forEach((comboItem: any) => {
        const comboItemLine = formatItemLine(
          `  - ${comboItem.product_name}`,
          comboItem.quantity || 1,
          comboItem.base_price || 0,
          width,
        )
        lines.push(comboItemLine)

        // Process modifiers for combo items
        if (comboItem.selected_modifiers) {
          Object.values(comboItem.selected_modifiers).forEach((modifierGroup: any) => {
            if (Array.isArray(modifierGroup)) {
              modifierGroup.forEach((modifier: any) => {
                const modPrice = modifier.price_modifier || 0
                const modQty = modifier.quantity || 1
                // Add modifier price * modifier quantity for EACH combo item
                modifiersTotal += modPrice * modQty
                const modifierLine = formatItemLine(
                  `    + ${modifier.name}`,
                  modQty,
                  modPrice,
                  width,
                )
                lines.push(modifierLine)
              })
            }
          })
        }
      })

      // Add calculation line if qty > 1 or has modifiers
      if (qty > 1 || modifiersTotal > 0) {
        const unitTotal = basePrice + modifiersTotal // modifiersTotal is per unit combo
        const finalTotal = (basePrice + modifiersTotal) * qty
        lines.push(formatCalculationLine(qty, unitTotal, finalTotal, width))
      }
    }
    else {
      // REGULAR PRODUCT: Standard handling
      const qty = cartItem.quantity || 1
      const basePrice = itemPrice
      let modifiersTotal = 0

      const mainLine = formatItemLine(
        cartItem.name,
        qty,
        basePrice,
        width,
      )
      lines.push(mainLine)

      // Process modifiers for regular products
      if (cartItem.modifiers && cartItem.modifiers.length > 0) {
        cartItem.modifiers.forEach((modifier) => {
          const selections = Array.isArray(modifier.selected)
            ? modifier.selected
            : (modifier.selected ? [modifier.selected] : [])

          selections.forEach((selection) => {
            if (selection) {
              const modPrice = selection.price_modifier || 0
              const modQty = selection.quantity || 1
              modifiersTotal += modPrice * modQty // modifier price * modifier quantity per unit
              subtotal += modPrice * modQty * qty // Still calculate total correctly for subtotal

              const modifierLine = formatItemLine(
                `  + ${selection.name}`,
                modQty,
                modPrice,
                width,
              )
              lines.push(modifierLine)
            }
          })
        })
      }

      // Add calculation line if qty > 1 or has modifiers
      if (qty > 1 || modifiersTotal > 0) {
        const unitTotal = basePrice + modifiersTotal // modifiersTotal is per unit
        const finalTotal = (basePrice + modifiersTotal) * qty
        lines.push(formatCalculationLine(qty, unitTotal, finalTotal, width))
      }
    }
  })

  lines.push('-'.repeat(width))

  // TOTALS
  if (orderData.discount) {
    lines.push(formatTotalLine(`${translate('print.receipt.subtotal', 'Subtotal')}:`, subtotal, width))

    // Display discount value as percentage
    lines.push(formatDiscountLine(`${translate('print.receipt.discount', 'Discount')}:`, orderData.discount, width))

    lines.push(`${LINE_PREFIXES.BOLD}${formatTotalLine(`${translate('print.receipt.total', 'TOTAL')}:`, orderData.order_total, width)}`)
  }
  else {
    lines.push(`${LINE_PREFIXES.BOLD}${formatTotalLine(`${translate('print.receipt.total', 'TOTAL')}:`, orderData.order_total, width)}`)
  }

  lines.push('')

  // PAYMENT METHOD
  if (orderData.payment_details?.name) {
    lines.push(`${translate('print.receipt.payment', 'Payment')}: ${orderData.payment_details.name}`)
  }

  lines.push('='.repeat(width))
  lines.push('')

  // FOOTER
  lines.push(`${LINE_PREFIXES.CENTER}${translate('print.receipt.thankYou', 'Thank you!')}`)
  lines.push(`${LINE_PREFIXES.CENTER}${translate('print.receipt.visitAgain', 'Visit us again!')}`)
  lines.push('')
  lines.push('')
  lines.push('') // Extra lines for paper cut

  return {
    lines,
    metadata: {
      type: 'receipt',
      cut: true,
      drawer: orderData.payment_details?.name?.toLowerCase().includes('cash'),
      business: businessInfo,
    },
  }
}

// Helper functions for formatting
function centerText(text: string, width: number): string {
  const padding = Math.max(0, Math.floor((width - text.length) / 2))
  return ' '.repeat(padding) + text
}

function formatItemLine(name: string, qty: number, unitPrice: number, width: number): string {
  // Format quantity and price (now showing unit price, not total)
  const qtyStr = qty.toString().padStart(3)
  const priceStr = unitPrice > 0 ? `$${unitPrice.toFixed(2)}`.padStart(8) : ''.padStart(8)

  // Calculate available space for item name
  const nameWidth = width - qtyStr.length - priceStr.length - 2 // -2 for spaces

  // Truncate name if too long
  const shortName = name.length > nameWidth ? name.substring(0, nameWidth - 3) + '...' : name

  // Calculate dots to fill the space
  const usedWidth = shortName.length + qtyStr.length + priceStr.length + 2
  const dotsCount = Math.max(0, width - usedWidth)

  return `${shortName}${'.'.repeat(dotsCount)} ${qtyStr} ${priceStr}`
}

function formatCalculationLine(qty: number, unitPrice: number, totalPrice: number, width: number): string {
  const calcText = `${qty} x $${unitPrice.toFixed(2)} = $${totalPrice.toFixed(2)}`
  const padding = Math.max(0, width - calcText.length)
  return `${LINE_PREFIXES.BOLD}${' '.repeat(padding)}${calcText}`
}

function formatTotalLine(label: string, amount: number, width: number): string {
  const amountStr = `$${amount.toFixed(0)}`
  const dotsCount = Math.max(0, width - label.length - amountStr.length)

  return `${label}${' '.repeat(dotsCount)}${amountStr}`
}

function formatDiscountLine(label: string, percentage: number, width: number): string {
  const percentageStr = `-${percentage}%`
  const dotsCount = Math.max(0, width - label.length - percentageStr.length)

  return `${label}${' '.repeat(dotsCount)}${percentageStr}`
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Future: Transform shift report to printable document
 */
export function transformShiftToDocument(shift: any, businessInfo?: BusinessInfo): PrintableDocument {
  const lines = [
    '================================',
    centerText('SHIFT REPORT', 32),
    '================================',
    `Shift ID: ${shift.id}`,
    `Start: ${formatDateTime(shift.start_time)}`,
    `End: ${shift.end_time ? formatDateTime(shift.end_time) : 'In Progress'}`,
    '--------------------------------',
    // Add shift details here
    '================================',
  ]

  return {
    lines,
    metadata: {
      type: 'report',
      cut: true,
      business: businessInfo,
    },
  }
}

/**
 * Future: Transform inventory report to printable document
 */
export function transformInventoryToDocument(inventory: any, businessInfo?: BusinessInfo): PrintableDocument {
  const lines = [
    '================================',
    centerText('INVENTORY REPORT', 32),
    '================================',
    `Date: ${new Date().toLocaleDateString()}`,
    '--------------------------------',
    'Product              Qty  Value',
    '--------------------------------',
    // Add inventory items here
    '================================',
  ]

  return {
    lines,
    metadata: {
      type: 'report',
      cut: true,
      business: businessInfo,
    },
  }
}
